import type {BoxSpacingValues} from '@primer/react-brand'
import {Box, Grid} from '@primer/react-brand'
import {isFeatureEnabled} from '@github-ui/feature-flags'

import type {FlexPage} from '../../../schemas/contentful/contentTypes/flexTemplate'
import {ContentfulBackgroundImage} from '../../../components/contentful/ContentfulBackgroundImage/ContentfulBackgroundImage'
import {ContentfulHero} from '../../../components/contentful/ContentfulHero/ContentfulHero'
import {ContentfulBreadcrumbs} from '../../../components/contentful/ContentfulBreadcrumbs/ContentfulBreadcrumbs'
import {ContentfulSubnav} from '../../../components/contentful/ContentfulSubnav/ContentfulSubnav'
import {useRef, useState} from 'react'
import {useResizeObserver} from '../../../lib/hooks/use-resize-observer'
import {usePersonalization} from '../../../contexts/PersonalizationContext'
import {evaluateRenderingStrategy} from '../../../lib/utils/rendering-strategy'

type SpacingValues = (typeof BoxSpacingValues)[number]

const heroPaddingValues: Record<string, SpacingValues> = {
  condensed: 80,
  spacious: 128,
  none: 'none',
}

export function FlexTemplateHero({page}: {page: FlexPage}) {
  const subNavRef = useRef<HTMLDivElement>(null)
  const {userState} = usePersonalization()

  // Initial subNavHeight set to 56px as this is more often the height of the Subnav component.
  // Things that can change this height are CTAs, dropdowns, and other elements that may be added to the Subnav.
  const [subNavHeight, setSubNavHeight] = useState<number>(56)

  const {breadcrumbs, hero, heroStrategies, heroBackgroundImage, subnav, visualSettings} = page.fields.template.fields
  const {heroPaddingBlockEnd: visualSettingsHeroPaddingBlockEnd, subNavOverlapsHeroBackgroundImage = true} =
    visualSettings?.fields ?? {}

  // Evaluate rendering strategies to determine which hero to render
  const isRenderingStrategyEnabled = isFeatureEnabled('flex_cta_groups_mvp')
  const strategyResult = isRenderingStrategyEnabled ? evaluateRenderingStrategy(heroStrategies, userState) : null

  // Use strategy-based hero if available, otherwise fall back to default hero
  const selectedHero = strategyResult?.component || hero
  const analyticsContext = strategyResult?.strategy

  const subNavColorMode = heroBackgroundImage?.fields.colorMode || page.fields.settings?.fields.colorMode
  const hasBackgroundImage = !!heroBackgroundImage
  const hasSubnav = !!subnav

  const heroPaddingBlockEnd = visualSettingsHeroPaddingBlockEnd
    ? heroPaddingValues[visualSettingsHeroPaddingBlockEnd]
    : undefined

  useResizeObserver(() => {
    if (subNavRef.current && !subNavOverlapsHeroBackgroundImage) {
      setSubNavHeight(subNavRef.current.offsetHeight)
    }
  })

  return (
    <>
      {subnav && (
        <ContentfulSubnav
          component={subnav}
          data-color-mode={subNavColorMode}
          hasShadow
          linkVariant="default"
          ref={subNavRef}
        />
      )}

      <Grid>
        <Grid.Column>
          <ContentfulBackgroundImage
            component={heroBackgroundImage}
            topOffset={hasSubnav && !subNavOverlapsHeroBackgroundImage ? subNavHeight : undefined}
            expand={hasBackgroundImage}
          >
            <Box paddingBlockStart={hasBackgroundImage && hasSubnav ? 64 : undefined}>
              {breadcrumbs && (
                <Box paddingBlockStart={hasBackgroundImage && hasSubnav ? 16 : hasSubnav ? 80 : 32}>
                  <ContentfulBreadcrumbs breadcrumbs={breadcrumbs} />
                </Box>
              )}
              <ContentfulHero
                data-hpc
                component={selectedHero}
                paddingBlockEnd={heroPaddingBlockEnd}
                analyticsContext={analyticsContext}
              />
            </Box>
          </ContentfulBackgroundImage>
        </Grid.Column>
      </Grid>
    </>
  )
}
